import type {
	ColorType,
	Options,
	OptionsStackingValue,
	PlotBarOptions,
} from 'highcharts';
import type { ChartCardConfig } from '../models/chart-config.model';
import type { DimensionConfig } from '../models/chart-config.model';
import { ChartOptionsService } from '../components/editors/jepto-chart-editor/chart-options.service';

/** Minimal series shape for breakdown detection from WS payloads. */
export interface CategoryBreakdownSeriesLike {
	id?: string;
	name?: string;
	label?: string;
}

/** Bar/column charts with a category breakdown dimension default to stacked segments. */
export function usesCategoryBreakdownChartType(chartType: string): boolean {
	const normalized = chartType?.trim().toLowerCase();
	return normalized === 'bar' || normalized === 'column';
}

export function hasCategoryBreakdownDimension(
	dimensions: DimensionConfig[] | undefined
): boolean {
	if (!Array.isArray(dimensions)) {
		return false;
	}
	const names = dimensions
		.map(dim => (typeof dim?.name === 'string' ? dim.name.trim() : ''))
		.filter(name => name.length > 0);
	return names.length >= 2;
}

/**
 * Detect breakdown fan-out from shaped chart data when card config dimensions
 * are unavailable at render time (e.g. report canvas uses WS payload only).
 */
export function hasCategoryBreakdownSeries(
	series: CategoryBreakdownSeriesLike[] | undefined
): boolean {
	if (!Array.isArray(series) || series.length < 2) {
		return false;
	}
	if (
		series.some(
			entry => typeof entry.id === 'string' && entry.id.includes('::')
		)
	) {
		return true;
	}
	const metricNames = series
		.map(entry => entry.name?.trim() ?? '')
		.filter(name => name.length > 0);
	if (metricNames.length < 2 || new Set(metricNames).size !== 1) {
		return false;
	}
	const labels = series.map(
		entry => entry.label?.trim() || entry.name?.trim() || ''
	);
	return labels.filter(Boolean).length >= 2 && new Set(labels).size >= 2;
}

export function shouldStackCategoryBreakdown(
	chartType: string,
	dimensions: DimensionConfig[] | undefined,
	series: CategoryBreakdownSeriesLike[] | undefined
): boolean {
	if (!usesCategoryBreakdownChartType(chartType)) {
		return false;
	}
	return (
		hasCategoryBreakdownDimension(dimensions) ||
		hasCategoryBreakdownSeries(series)
	);
}

/**
 * Resolve Highcharts stacking for bar/column from Chart-tab visualization
 * (`plotOptions.series` and/or type plotOptions), stacked input, or category
 * breakdown default. Prefer explicit viz values (including `percent`).
 */
export function resolveCartesianStacking(
	chartType: 'bar' | 'column',
	options: {
		stackedInput?: boolean;
		categoryBreakdown: boolean;
		visualization?: Options | null;
		baseOptions?: Options | null;
	}
): OptionsStackingValue | undefined {
	const typeKey = chartType;
	const readStacking = (opts: Options | null | undefined) => {
		if (!opts?.plotOptions) {
			return undefined;
		}
		const typeStacking = (
			opts.plotOptions[typeKey] as { stacking?: OptionsStackingValue }
		)?.stacking;
		const seriesStacking = (
			opts.plotOptions.series as { stacking?: OptionsStackingValue }
		)?.stacking;
		return typeStacking ?? seriesStacking;
	};

	const explicit =
		readStacking(options.visualization) ?? readStacking(options.baseOptions);

	if (options.categoryBreakdown) {
		return explicit ?? ('normal' as OptionsStackingValue);
	}
	if (options.stackedInput) {
		return explicit ?? ('normal' as OptionsStackingValue);
	}
	return explicit;
}

function cartesianPlotOptionsTypeKey(
	chartType: string
): 'bar' | 'column' | null {
	const normalized = chartType?.trim().toLowerCase();
	if (normalized === 'bar') return 'bar';
	if (normalized === 'column') return 'column';
	return null;
}

/**
 * Highcharts reads stacking from the series-type plotOptions (`bar` / `column`),
 * not only `plotOptions.series`. Apply to both so stacked breakdown segments
 * render with distinct palette colors.
 */
export function applyCategoryBreakdownStackingToOptions(
	options: Options,
	chartType: string,
	enableStacking: boolean
): Options {
	if (!enableStacking) {
		return options;
	}
	const typeKey = cartesianPlotOptionsTypeKey(chartType);
	if (!typeKey) {
		return options;
	}
	const stacking = 'normal' as OptionsStackingValue;
	const typePlotOptions = (options.plotOptions?.[typeKey] ??
		{}) as PlotBarOptions;
	return {
		...options,
		plotOptions: {
			...(options.plotOptions ?? {}),
			series: {
				...(options.plotOptions?.series ?? {}),
				stacking,
			},
			[typeKey]: {
				...typePlotOptions,
				stacking,
				colorByPoint: false,
			},
		},
	};
}

/** Resolve series color from palette when breakdown segments share one metric. */
export function categoryBreakdownSeriesColor(
	paletteColors: readonly ColorType[],
	seriesIndex: number,
	explicitColor?: string
): ColorType | undefined {
	if (explicitColor?.trim()) {
		return explicitColor;
	}
	if (!paletteColors.length) {
		return undefined;
	}
	return paletteColors[seriesIndex % paletteColors.length];
}

/**
 * Build override-only visualization patch when category breakdown toggles.
 * Enables `plotOptions.series.stacking: normal` when breakdown is set; clears
 * auto-applied normal stacking when breakdown is removed.
 */
export function buildCategoryBreakdownVisualizationPatch(
	chartType: string,
	hasBreakdown: boolean,
	currentVisualization: ChartCardConfig['visualization'] | undefined,
	chartOptionsService: ChartOptionsService,
	cardConfig?: ChartCardConfig | null
): ChartCardConfig['visualization'] | undefined {
	if (!usesCategoryBreakdownChartType(chartType)) {
		return undefined;
	}

	const typeKey = cartesianPlotOptionsTypeKey(chartType);
	if (!typeKey) {
		return undefined;
	}

	const current =
		currentVisualization && typeof currentVisualization === 'object'
			? (currentVisualization as Options)
			: {};

	const stacking = hasBreakdown
		? ('normal' as OptionsStackingValue)
		: undefined;
	const currentTypePlotOptions = (current.plotOptions?.[typeKey] ??
		{}) as PlotBarOptions;

	const merged: Options = {
		...current,
		plotOptions: {
			...(current.plotOptions ?? {}),
			series: {
				...(current.plotOptions?.series ?? {}),
				stacking,
			},
			[typeKey]: {
				...currentTypePlotOptions,
				stacking,
				...(hasBreakdown ? { colorByPoint: false } : {}),
			},
		},
	};

	return chartOptionsService.stripToVisualizationOverrides(
		merged,
		chartType,
		cardConfig ?? null
	);
}
